import { supabase } from '../lib/supabase';

// ============================================
// NOTIFICATION SERVICE
// ============================================

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    userId: obj?.user_id,
    notificationType: obj?.notification_type,
    title: obj?.title,
    message: obj?.message,
    metadata: obj?.metadata,
    isRead: obj?.is_read,
    readAt: obj?.read_at,
    createdAt: obj?.created_at
  };
};

/**
 * Fetch all notifications for current user
 * @param {Object} filters - Filter options (type, isRead, limit)
 * @returns {Promise} Promise resolving to notifications data
 */
export const fetchNotifications = async (filters = {}) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    let query = supabase
      ?.from('notifications')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false });

    // Apply filters
    if (filters?.type && filters?.type !== 'all') {
      query = query?.eq('notification_type', filters?.type);
    }

    if (filters?.isRead !== undefined) {
      query = query?.eq('is_read', filters?.isRead);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    } else {
      query = query?.limit(100);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Convert to camelCase
    const camelCaseData = data?.map(toCamelCase);

    return { data: camelCaseData, error: null };
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return { data: null, error: error?.message };
  }
};

/**
 * Mark notification as read
 * @param {String} notificationId - Notification ID
 * @returns {Promise} Promise resolving to updated notification
 */
export const markAsRead = async (notificationId) => {
  try {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true, read_at: new Date()?.toISOString() })
      ?.eq('id', notificationId)
      ?.select()
      ?.single();

    if (error) throw error;

    return { data: toCamelCase(data), error: null };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { data: null, error: error?.message };
  }
};

/**
 * Mark all notifications as read
 * @returns {Promise} Promise resolving to updated notifications
 */
export const markAllAsRead = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true, read_at: new Date()?.toISOString() })
      ?.eq('user_id', user?.id)
      ?.eq('is_read', false)
      ?.select();

    if (error) throw error;

    return { data: data?.map(toCamelCase), error: null };
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return { data: null, error: error?.message };
  }
};

/**
 * Delete notification
 * @param {String} notificationId - Notification ID
 * @returns {Promise} Promise resolving to deletion result
 */
export const deleteNotification = async (notificationId) => {
  try {
    const { error } = await supabase
      ?.from('notifications')
      ?.delete()
      ?.eq('id', notificationId);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Error deleting notification:', error);
    return { error: error?.message };
  }
};

/**
 * Get unread notification count
 * @returns {Promise} Promise resolving to unread count
 */
export const getUnreadCount = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      return { count: 0, error: null };
    }

    const { count, error } = await supabase
      ?.from('notifications')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('user_id', user?.id)
      ?.eq('is_read', false);

    if (error) throw error;

    return { count: count || 0, error: null };
  } catch (error) {
    console.error('Error getting unread count:', error);
    return { count: 0, error: error?.message };
  }
};

/**
 * Subscribe to real-time notification updates
 * @param {Function} callback - Callback function to handle notification updates
 * @returns {Object} Supabase channel object
 */
export const subscribeToNotifications = (callback) => {
  const channel = supabase
    ?.channel('user_notifications')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notifications'
      },
      (payload) => {
        console.log('Notification update detected:', payload);
        callback(payload);
      }
    )
    ?.subscribe();
  
  return channel;
};

/**
 * Unsubscribe from notification channel
 * @param {Object} channel - Supabase channel object to unsubscribe
 */
export const unsubscribeFromNotifications = (channel) => {
  if (channel) {
    supabase?.removeChannel(channel);
  }
};

export default {
  fetchNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  getUnreadCount,
  subscribeToNotifications,
  unsubscribeFromNotifications
};